// app-subdomain-creators/src/services/creatorEngagementService.js

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;

function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null) return;
    const trimmed = String(value).trim();
    if (!trimmed) return;
    query.set(key, trimmed);
  });
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

async function requestJson(path, options = {}) {
  const method = options.method || 'GET';
  const query = buildQuery(options.query || {});
  const url = `${apiProxyUrl}${path}${query}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: method === 'GET' ? undefined : JSON.stringify(options.body || {}),
  });

  const raw = await response.text().catch(() => '');
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_err) {
    data = null;
  }

  if (!response.ok) {
    const message =
      (data && (data.error || data.message || data.details)) ||
      raw ||
      `Request failed (${response.status})`;
    throw new Error(String(message));
  }

  return data;
}

export async function fetchCreatorActivities(params = {}) {
  return requestJson('/api/creator-activities', {
    method: 'GET',
    query: params,
  });
}

export async function fetchQuizAnalytics(params = {}) {
  return requestJson('/api/creator-quiz-analytics', {
    method: 'GET',
    query: params,
  });
}

export async function trackCreatorActivity(payload = {}) {
  return requestJson('/api/creator-activities', {
    method: 'POST',
    body: payload,
  });
}

export async function fetchMemberCourseProgress(memberId, params = {}) {
  return requestJson(`/api/creator-course-progress/member/${encodeURIComponent(String(memberId || ''))}`, {
    method: 'GET',
    query: params,
  });
}

export async function fetchCourseProgressOverview(params = {}) {
  return requestJson('/api/creator-course-progress/overview', {
    method: 'GET',
    query: params,
  });
}

export async function fetchVideoChannelAnalytics(params = {}) {
  return requestJson('/api/creator-video-channel/analytics/video-channels', {
    method: 'GET',
    query: params,
  });
}

export async function fetchPodcastAnalytics(params = {}) {
  return requestJson('/api/creator-podcast-analytics', {
    method: 'GET',
    query: params,
  });
}

export async function fetchFlixTheatreAnalytics(params = {}) {
  return requestJson('/api/creator-flix-theatre/analytics/flix-theatre', {
    method: 'GET',
    query: params,
  });
}

export async function fetchStorefrontAnalytics(params = {}) {
  const query = buildQuery(params || {});
  const requestPaths = [
    '/api/analytics/stores',
    '/api/creators/analytics/stores',
  ];
  const requestBases = Array.from(
    new Set(
      [apiProxyUrl, apiBackendUrl]
        .map((value) => String(value || '').trim().replace(/\/+$/, ''))
        .filter(Boolean)
    )
  );

  let lastError = 'Failed to load stores analytics';

  for (const base of requestBases) {
    for (const path of requestPaths) {
      const response = await fetch(`${base}${path}${query}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const raw = await response.text().catch(() => '');
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch (_err) {
        data = null;
      }

      if (response.ok) {
        return data;
      }

      const routeMiss = raw
        && String(raw).toLowerCase().includes('<!doctype html')
        && String(raw).toLowerCase().includes('cannot get');
      if (routeMiss || response.status === 404) {
        continue;
      }

      lastError =
        (data && (data.error || data.message || data.details))
        || raw
        || `Request failed (${response.status})`;
      break;
    }
  }

  throw new Error(String(lastError));
}

export async function fetchCryptoAnalytics(params = {}) {
  const query = buildQuery(params || {});
  const requestPaths = [
    '/api/analytics/crypto',
    '/api/creators/analytics/crypto',
  ];
  const requestBases = Array.from(
    new Set(
      [apiProxyUrl, apiBackendUrl]
        .map((value) => String(value || '').trim().replace(/\/+$/, ''))
        .filter(Boolean)
    )
  );

  let lastError = 'Failed to load crypto analytics';

  for (const base of requestBases) {
    for (const path of requestPaths) {
      const response = await fetch(`${base}${path}${query}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const raw = await response.text().catch(() => '');
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch (_err) {
        data = null;
      }

      if (response.ok) {
        return data;
      }

      const routeMiss = raw
        && String(raw).toLowerCase().includes('<!doctype html')
        && String(raw).toLowerCase().includes('cannot get');
      if (routeMiss || response.status === 404) {
        continue;
      }

      lastError =
        (data && (data.error || data.message || data.details))
        || raw
        || `Request failed (${response.status})`;
      break;
    }
  }

  throw new Error(String(lastError));
}

export async function fetchFeaturePaywallAnalytics(params = {}) {
  const query = buildQuery(params || {});
  const requestPaths = [
    '/api/creators/analytics/feature-paywall',
    '/api/analytics/feature-paywall',
  ];
  const requestBases = Array.from(
    new Set(
      [apiProxyUrl, apiBackendUrl]
        .map((value) => String(value || '').trim().replace(/\/+$/, ''))
        .filter(Boolean)
    )
  );

  let lastError = 'Failed to load feature payment analytics';

  for (const base of requestBases) {
    for (const path of requestPaths) {
      const response = await fetch(`${base}${path}${query}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const raw = await response.text().catch(() => '');
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch (_err) {
        data = null;
      }

      if (response.ok) {
        return data;
      }

      const routeMiss = raw
        && String(raw).toLowerCase().includes('<!doctype html')
        && String(raw).toLowerCase().includes('cannot get');
      if (routeMiss || response.status === 404) {
        continue;
      }

      lastError =
        (data && (data.error || data.message || data.details))
        || raw
        || `Request failed (${response.status})`;
      break;
    }
  }

  throw new Error(String(lastError));
}

export async function fetchMembersAnalytics(params = {}) {
  return requestJson('/api/analytics/members', {
    method: 'GET',
    query: params,
  });
}
